// Shared reporting for the error boundaries (global-error.tsx and error.tsx).
// Boundaries render outside/instead of the normal tree, so identity and route
// history come from the sessionStorage breadcrumbs, not React context.

import {
  getLastKnownUser,
  getRecentConsoleErrors,
  getRouteBreadcrumbs,
} from "./errorBreadcrumbs";
import { getRenderStormEvents } from "@/app/_hooks/useRenderStormProbe";

export type BoundaryError = Error & { digest?: string };

const CHUNK_RELOAD_KEY = "mm-chunk-reload-at";
const CHUNK_RELOAD_COOLDOWN_MS = 2 * 60 * 1000;

// Stale-deploy chunk failures: a tab opened before a deploy requests a JS
// chunk that no longer exists on the server. A reload always fixes it.
export const isChunkLoadError = (error: BoundaryError) =>
  error?.name === "ChunkLoadError" ||
  // Covers webpack ("Loading chunk N failed") and Turbopack
  // ("Failed to load chunk ... from module ...") phrasings
  /loading chunk .* failed|failed to load chunk|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|css chunk/i.test(
    error?.message ?? ""
  );

// True at most once per cooldown window, so a genuinely broken chunk shows
// the error page instead of reload-looping. Read-only so it is safe to call
// during render; reportAndReloadForChunkError starts the cooldown.
export const shouldAutoReloadForChunkError = (error: BoundaryError) => {
  if (!isChunkLoadError(error)) return false;
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
    return Date.now() - last >= CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
};

// Stale-chunk recovery: reload once the report is sent, capped so a slow
// email send never holds the user on the loader
const CHUNK_RELOAD_MAX_WAIT_MS = 2000;

export const reportAndReloadForChunkError = (
  error: BoundaryError,
  boundary: string
) => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {}
  Promise.race([
    reportBoundaryError(error, `${boundary} (stale chunk, auto-reloaded)`),
    new Promise((resolve) => setTimeout(resolve, CHUNK_RELOAD_MAX_WAIT_MS)),
  ]).finally(() => window.location.reload());
};

export const reportBoundaryError = async (
  error: BoundaryError,
  boundary: string
): Promise<void> => {
  try {
    let userId = "Unknown";
    let userName = "Unknown";

    // Last user doc loaded this tab — survives the React tree teardown
    const cached = getLastKnownUser();
    if (cached) {
      userId = cached.userId;
      userName = `${cached.businessName} <${cached.email}>${
        cached.authEmail && cached.authEmail !== cached.email
          ? ` (logged in as ${cached.authEmail})`
          : ""
      }`;
    }

    // Cross-check against the live Firebase Auth session; imported lazily so
    // a Firebase failure can never break the error page itself
    try {
      const [{ getAuth }, { app }] = await Promise.all([
        import("firebase/auth"),
        import("@/app/_firebase/firebase"),
      ]);
      const authUser = getAuth(app).currentUser;
      if (authUser && authUser.uid !== userId) {
        userId = authUser.uid;
        userName = `${
          authUser.displayName || authUser.email || "Unknown"
        } (from auth session)`;
      }
    } catch {}

    // keepalive lets the request survive an immediate reload/navigation
    await fetch("/api/email/errorNotification", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        userName,
        userId,
        boundary,
        location: window.location.href,
        userAgent: navigator.userAgent,
        routeHistory: getRouteBreadcrumbs(),
        consoleTail: getRecentConsoleErrors(),
        renderStorms: getRenderStormEvents(),
        occurredAt: new Date().toISOString(),
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest,
        },
      }),
    }).catch(() => {});
  } catch {
    // Reporting must never throw
  }
};
